import { AddToListMenu_Query } from '#app/__generated__/relay/AddToListMenu_Query.graphql.js';
import {
  AddToListMenu_userAccount$key,
  ExpertType,
} from '#app/__generated__/relay/AddToListMenu_userAccount.graphql.js';
import { useModal } from '#app/components/Modals/V2/GlobalModalSystem/useModal.js';
import { useToast } from '#app/components/Toasts/hooks/useToast.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useIsMobile } from '#app/hooks/useIsMobile.js';
import { useSaveUserAsContact } from '#app/hooks/useSaveUserAsContact.js';
import { useUserListMutations } from '../hooks/useUserListMutations.js';
import { AddToListMenuPrimitive } from './AddToListMenuPrimitive.js';
import { type AddToListMenuReturn } from './useAddToListMenu.js';
import { useAddToListMutations } from './useAddToListMutations.js';
import {
  CreateContactForUserProfileSource,
  UpdateUsersMembershipsInUserListsInput,
} from '@contra/graphql-types';
import { graphql, useFragment, useLazyLoadQuery } from '@contra/relay';
import { captureException } from '@contra/sentry/react';
import {
  ReactNode,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

type AddToListMenuProps = {
  readonly addToListMenuProps: AddToListMenuReturn;
  readonly analyticsSection: string;
  readonly contactId: null | string;
  readonly onClose?: () => void;
  readonly onListChecked?: (listIds: string[]) => void;
  readonly onListDeleted?: (listId: string) => void;
  readonly onListsUpdatedSuccess?: () => void;
  readonly onSave?: () => void;
  readonly onSaveSuccess?: () => void;
  readonly onUnsave?: () => void;
  readonly onUnsaveSuccess?: () => void;
  readonly showListsMenuOnly?: boolean;
  readonly source: CreateContactForUserProfileSource;
  readonly trigger: ReactNode;
  readonly userProfile: {
    firstName: string;
    id: string;
    isSavedContact: boolean;
  };
  readonly warningMessage?: string;
};

const getCheckedListIds = (
  availableLists: Array<{ id: string; isUserProfileInList: boolean }>,
) => {
  return availableLists
    .filter((list) => list.isUserProfileInList)
    .map((list) => list.id);
};

const maybePluralize = (word: string, count: number) => {
  return count === 1 ? word : `${word}s`;
};

type AvailableList = {
  readonly displayName: string;
  readonly expertProgram: null | {
    readonly expertPartnership: ExpertType;
    readonly name: string;
  };
  readonly id: string;
  readonly isUserProfileInList: boolean;
};

const LazyListDataFetcher = ({
  setAvailableLists,
  userProfileId,
}: {
  readonly setAvailableLists: (lists: AvailableList[]) => void;
  readonly userProfileId: string;
}) => {
  const queryData = useLazyLoadQuery<AddToListMenu_Query>(
    graphql`
      query AddToListMenu_Query($userProfileId: ID!) {
        visitor {
          userAccount {
            ...AddToListMenu_userAccount
              @arguments(userProfileId: $userProfileId)
          }
        }
      }
    `,
    { userProfileId },
    { fetchPolicy: 'store-or-network' },
  );

  const userAccount = useFragment<AddToListMenu_userAccount$key>(
    graphql`
      fragment AddToListMenu_userAccount on UserAccount
      @argumentDefinitions(userProfileId: { type: "ID!" }) {
        workspaceForSelectedUserType {
          ... on ClientWorkspace {
            __typename
            userLists(first: 200, filter: { groupNid: NETWORK_LIST }) {
              edges {
                node {
                  id
                  displayName
                  expertProgram {
                    expertPartnership
                    name
                  }
                  isUserProfileInList(userProfileId: $userProfileId)
                }
              }
            }
          }
        }
      }
    `,
    queryData.visitor.userAccount,
  );

  const availableLists: AvailableList[] = useMemo(
    () =>
      userAccount?.workspaceForSelectedUserType?.__typename ===
      'ClientWorkspace'
        ? userAccount.workspaceForSelectedUserType.userLists.edges.map(
            (edge) => edge.node,
          )
        : [],
    [userAccount?.workspaceForSelectedUserType],
  );

  useEffect(() => {
    setAvailableLists(availableLists);
  }, [availableLists, queryData.visitor.userAccount, setAvailableLists]);

  return null;
};

export const AddToListMenu = ({
  addToListMenuProps,
  analyticsSection,
  contactId,
  onClose,
  onListChecked,
  onListDeleted,
  onListsUpdatedSuccess,
  onSave,
  onSaveSuccess,
  onUnsave,
  onUnsaveSuccess,
  showListsMenuOnly,
  source,
  trigger,
  userProfile,
  warningMessage,
}: AddToListMenuProps) => {
  const toast = useToast();
  const { track } = useAnalytics();
  const isMobile = useIsMobile();

  const isSaved = userProfile.isSavedContact;

  const [availableLists, setAvailableLists] = useState<AvailableList[]>([]);

  const initialCheckedListIds = useMemo(() => {
    return getCheckedListIds(availableLists);
  }, [availableLists]);

  const [checkedListIds, setCheckedListIds] = useState(
    new Set(initialCheckedListIds),
  );

  const lists = availableLists.map((list) => ({
    id: list.id,
    isExpertList: Boolean(list.expertProgram),
    name: list.displayName,
  }));

  const selectedLists = lists.filter((list) => checkedListIds.has(list.id));

  const previousSelectedListIdsRef = useRef<string[]>(
    selectedLists.map((list) => list.id),
  );
  const previousSelectedListIds = previousSelectedListIdsRef.current;

  const { updateListMemberships } = useAddToListMutations();

  const handleUpdateListMemberships = useCallback(
    ({
      input,
      onSuccess,
    }: {
      input: UpdateUsersMembershipsInUserListsInput;
      onSuccess: (newCheckedListIds: string[]) => void;
    }) => {
      updateListMemberships({
        onCompleted: (errors, response) => {
          const { apiErrors, hasErrors, payloadErrors } = errors;
          if (hasErrors) {
            if (payloadErrors) {
              captureException(payloadErrors[0]);
              toast.error(
                'Sorry, something went wrong. Please try again or get in touch with support for assistance.',
              );
            } else if (apiErrors) {
              toast.error(apiErrors[0].message);
            }
          } else {
            const updatedLists =
              response?.updateUsersMembershipsInUserLists?.visitor?.userAccount?.userLists?.edges?.map(
                (edge) => edge?.node,
              ) ?? [];
            const newCheckedListIds = getCheckedListIds(updatedLists);

            onSuccess(newCheckedListIds);

            setCheckedListIds(new Set(newCheckedListIds));
            previousSelectedListIdsRef.current = newCheckedListIds;
          }
        },
        onError: (error) => {
          captureException(error);
        },
        variables: {
          input,
          userProfileId: userProfile.id,
        },
      });
    },
    [toast, updateListMemberships, userProfile.id],
  );

  const { removeUserAsContact, saveUserAsContact } = useSaveUserAsContact({
    analyticsSection,
    contactId,
    source,
    userProfile: {
      ...userProfile,
      // this is not used in V2, can be removed from the hook later
      avatarImage: null,
      fullName: '',
    },
  });

  const { createUserList, updateUserList } = useUserListMutations();

  const [interactedUserListId, setInteractedUserListId] = useState<
    null | string
  >(null);

  const interactedUserList =
    availableLists.find((list) => list.id === interactedUserListId) ?? null;

  const [confirmedExpertListIds, setConfirmedExpertListIds] = useState<
    string[]
  >([]);

  const updateLists = useCallback(
    (userListIdsToAddTo: string[], userListIdsToRemoveFrom: string[]) => {
      handleUpdateListMemberships({
        input: {
          userListIdsToAddTo,
          userListIdsToRemoveFrom,
          userProfileIds: [userProfile.id],
        },
        onSuccess: (newCheckedListIds) => {
          const hasChanged =
            newCheckedListIds.length !== previousSelectedListIds.length ||
            newCheckedListIds.some(
              (id) => !previousSelectedListIds.includes(id),
            ) ||
            previousSelectedListIds.some(
              (id) => !newCheckedListIds.includes(id),
            );

          if (hasChanged) {
            onListsUpdatedSuccess?.();
            toast.success(`${userProfile.firstName}'s lists have been updated`);
          }
        },
      });
    },
    [
      handleUpdateListMemberships,
      onListsUpdatedSuccess,
      previousSelectedListIds,
      toast,
      userProfile.firstName,
      userProfile.id,
    ],
  );

  const {
    isModalOpen: isConfirmExpertModalOpen,
    openModal: openConfirmExpertModal,
  } = useModal({
    identifier: 'GenericConfirmationModal',
    modalProps: {
      ariaLabel: 'Confirm add expert',
      confirmButton: {
        analytics: {
          button_name: 'add_to_expert_network_list',
          section: `${analyticsSection}_add_to_list_menu`,
        },
        label: 'Confirm',
        onClick: () => {
          if (interactedUserList) {
            setConfirmedExpertListIds([
              ...confirmedExpertListIds,
              interactedUserList.id,
            ]);

            const newSelectedListIds = [
              interactedUserList.id,
              ...selectedLists.map((list) => list.id),
            ];

            setCheckedListIds(new Set(newSelectedListIds));

            if (isMobile) {
              updateLists(
                newSelectedListIds,
                initialCheckedListIds.filter(
                  (id) => !newSelectedListIds.includes(id),
                ),
              );
            }

            setInteractedUserListId(null);
          }
        },
      },
      content: `By confirming, they will be notified and given an expert badge, as well as added to the ${interactedUserList?.expertProgram?.name} Expert Program.`,
      dismissButton: {
        analytics: {
          button_name: 'cancel',
          section: `${analyticsSection}_add_to_list_menu`,
        },
        label: 'Cancel',
        onClick: () => {
          setInteractedUserListId(null);
        },
      },
      heading: `Confirm ${userProfile.firstName} as ${interactedUserList?.expertProgram?.name} Expert`,
    },
  });

  const handleOnSelectedItemChange = useCallback(
    (newLists: Array<{ id: string; isExpertList: boolean; name: string }>) => {
      if (newLists.length > 0) {
        setInteractedUserListId(newLists[newLists.length - 1].id);
        onListChecked?.(newLists.map((list) => list.id));
      }

      const expertListsToConfirm = newLists.filter(
        (list) =>
          list.isExpertList &&
          !confirmedExpertListIds.includes(list.id) &&
          !availableLists.find((availableList) => availableList.id === list.id)
            ?.isUserProfileInList,
      );

      if (expertListsToConfirm.length > 0) {
        setInteractedUserListId(expertListsToConfirm[0].id);

        openConfirmExpertModal();

        if (isMobile) {
          addToListMenuProps.handleCloseListMenu();
        }

        return;
      }

      setCheckedListIds(new Set(newLists.map((list) => list.id)));
    },
    [
      addToListMenuProps,
      availableLists,
      confirmedExpertListIds,
      isMobile,
      onListChecked,
      openConfirmExpertModal,
    ],
  );

  const { isModalOpen: isDeleteListModalOpen, openModal: openDeleteListModal } =
    useModal({
      identifier: 'DeleteUserListModal',
      modalProps: {
        listName: interactedUserList?.displayName ?? 'List',
        onSuccess: () => {
          onListDeleted?.(interactedUserList?.id ?? '');

          toast.success(
            interactedUserList?.displayName
              ? `Your list, ${interactedUserList?.displayName}, has been deleted`
              : `Your list has been deleted`,
          );
          setInteractedUserListId(null);
        },
        source: 'add_to_list_menu',
        userListId: interactedUserList?.id ?? '',
      },
    });

  const {
    isModalOpen: isUnsaveModalOpen,
    openModal: openUnsaveConfirmationModal,
  } = useModal({
    identifier: 'DiscardConfirmationModal',
    modalProps: {
      analyticsSection: `${analyticsSection}_add_to_list_menu`,
      body: interactedUserList?.expertProgram
        ? `When you unsave a freelancer, they will no longer appear in any of your lists, their profile will be removed from published expert pages, and any expert badges they hold will be revoked.`
        : `When you unsave a freelancer, they will no longer appear in any of your lists, but their profile will remain archived in your network.`,
      continueLabel: 'Cancel',
      discardLabel: 'Unsave',
      header: `Unsave ${userProfile.firstName}?`,
      onDiscard: () => {
        onUnsave?.();

        track('button', {
          action: 'tapped',
          button_name: 'remove_contact',
          section: analyticsSection,
          switch_value: true,
        });
        removeUserAsContact({});

        const areAnyRemovedListsExpert = availableLists.some(
          (list) => list.expertProgram,
        );

        handleUpdateListMemberships({
          input: {
            userListIdsToAddTo: [],
            userListIdsToRemoveFrom: availableLists.map((list) => list.id),
            userProfileIds: [userProfile.id],
          },
          onSuccess: () => {
            onUnsaveSuccess?.();

            if (areAnyRemovedListsExpert) {
              toast.success(
                `${userProfile.firstName} was unsaved and removed from an expert list`,
              );
            } else {
              toast.success(`${userProfile.firstName} was unsaved`);
            }
          },
        });
      },
    },
  });

  const handleOnSave = useCallback(() => {
    onSave?.();

    track('button', {
      action: 'tapped',
      button_name: 'add_contact',
      section: analyticsSection,
      switch_value: true,
    });

    saveUserAsContact({
      onSuccess: onSaveSuccess,
    });
  }, [analyticsSection, onSave, onSaveSuccess, saveUserAsContact, track]);

  const handleOnUnsave = useCallback(() => {
    openUnsaveConfirmationModal();
  }, [openUnsaveConfirmationModal]);

  const handleOnClose = useCallback(() => {
    onClose?.();

    const selectedListIds = selectedLists.map((list) => list.id);

    if (isSaved) {
      updateLists(
        selectedListIds,
        initialCheckedListIds.filter((id) => !selectedListIds.includes(id)),
      );
    } else if (selectedLists.length > 0 && !isSaved) {
      handleOnSave();

      handleUpdateListMemberships({
        input: {
          userListIdsToAddTo: selectedListIds,
          userListIdsToRemoveFrom: [],
          userProfileIds: [userProfile.id],
        },
        onSuccess: () => {
          const areAnyAddedListsExpert = selectedLists.some(
            (list) => list.isExpertList,
          );

          toast.success(
            `${userProfile.firstName} was saved to ${checkedListIds.size} ${maybePluralize(`list`, checkedListIds.size)}${areAnyAddedListsExpert ? ` and added as an expert` : ``}`,
          );
        },
      });
    }
  }, [
    checkedListIds.size,
    handleUpdateListMemberships,
    initialCheckedListIds,
    isSaved,
    onClose,
    handleOnSave,
    selectedLists,
    toast,
    updateLists,
    userProfile.firstName,
    userProfile.id,
  ]);

  const handleOnListDeleted = useCallback(
    (listId: string) => {
      setInteractedUserListId(listId);
      openDeleteListModal();

      if (isMobile) {
        addToListMenuProps.handleCloseListMenu();
        addToListMenuProps.handleCloseActionsMenu();
      }
    },
    [addToListMenuProps, isMobile, openDeleteListModal],
  );

  const handleOnListNameUpdated = useCallback(
    (listId: string, newName: string) => {
      updateUserList({
        displayName: newName,
        userListId: listId,
      });

      track('button', {
        action: 'tapped',
        button_name: 'network_rename_user_list',
        section: analyticsSection,
      });
    },
    [analyticsSection, track, updateUserList],
  );

  const handleOnNewListCreated = useCallback(
    (name: string) => {
      createUserList({
        name,
        onSuccess: (listId) => {
          setCheckedListIds(new Set([listId, ...checkedListIds]));
        },
      });
      track('button', {
        action: 'tapped',
        button_name: 'network_create_user_list',
        section: analyticsSection,
      });
    },
    [analyticsSection, checkedListIds, createUserList, track],
  );

  return (
    <>
      {addToListMenuProps.isActionsMenuOpen ||
      addToListMenuProps.isListMenuOpen ? (
        <Suspense>
          <LazyListDataFetcher
            setAvailableLists={setAvailableLists}
            userProfileId={userProfile.id}
          />
        </Suspense>
      ) : null}
      <AddToListMenuPrimitive
        addToListMenuProps={addToListMenuProps}
        analyticsSection={analyticsSection}
        availableLists={lists}
        isModalOpen={
          isConfirmExpertModalOpen || isDeleteListModalOpen || isUnsaveModalOpen
        }
        isSaved={isSaved}
        onClose={handleOnClose}
        onListDeleted={handleOnListDeleted}
        onListNameUpdated={handleOnListNameUpdated}
        onNewListCreated={handleOnNewListCreated}
        onSelectedItemChange={handleOnSelectedItemChange}
        onUnsave={handleOnUnsave}
        selectedLists={selectedLists}
        showListsMenuOnly={showListsMenuOnly}
        trigger={trigger}
        warningMessage={warningMessage}
      />
    </>
  );
};
